/* eslint-disable operator-linebreak */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */

import { ReactElement, useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Formik, Form, FormikProps, validateYupSchema, getIn } from 'formik';
import { defaultForm, getReceiptPayload, getSubmissionPayload, iCourtFormValues, CourtSchema, courtMetaData } from '@services/CourtService';
import { FormContainer } from '@pages';
import ReactGA from 'react-ga';
import { FormNavFooter, FormLoading, Alert, ErrorPage, ProgressBar } from '@components';
import { FormType } from '@constants';
import { iToolTips, iCountry, iFormNavActions, iApiError, iPartialFormData } from '@interfaces/interfaces';
import { getCountryList, getTooltips, savePartialForm, submitSavedPartialForm } from '@api';
import { navigateToError, navigateToNextStep, getStringIndex, getDateForValidateDebtor, navToError } from '@utils';
import ErrorContent from '@components/Content/AlertContent/ErrorContent';
import { CourtStep0, CourtStep1, CourtStep2, CourtStep3, CourtStep4, CourtStep5, CourtStep6, CourtStep7, CourtStep8, CourtStep9, CourtStep10, CourtStep11, CourtStep12, CourtErrorPage } from '.';
import VicFinesContext from '../VicFinesFormContext';
import IssueIcon from '../../../images/issue.svg';

const { REACT_APP_SITECORE_API_ENDPOINT: API_URL, REACT_APP_PATH_COURT: PATH_COURT } = process.env;

function Court(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const pathElementArray = location.pathname.split('/');
  const stepIndex = getStringIndex(pathElementArray, 'step');
  const stepIndexDiff = stepIndex - pathElementArray.length;
  // example returned [step0,3224242] or [step0]
  const stepArray = pathElementArray.slice(stepIndexDiff);
  // example returned 0
  const stepIndexInPath = getStringIndex(stepArray, 'step');
  // always get the element contain step like step0
  const getStepInPath = stepArray[stepIndexInPath];
  const pathNameLessLastSegment = stepIndex ? location.pathname.split('/').slice(0, stepIndexDiff).join('/') : location.pathname;
  const currentStep = location.pathname === `${PATH_COURT}` || location.pathname === `${PATH_COURT}/` ? 'step0' : `${getStepInPath}`;

  const [loading, setLoading] = useState(true);
  const [countryList, setCountryList] = useState<iCountry[]>([]);
  const [tooltips, setTooltips] = useState<iToolTips>({} as iToolTips);
  const [apiError, setApiError] = useState<iApiError>({} as iApiError);
  const formikValuesRef = useRef({} as FormikProps<iCourtFormValues>);
  const [activeError, setActiveError] = useState(false);
  type obligationNum = {
    ob: string;
  };
  const { ob } = useParams<obligationNum>();
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [countryResults, tooltipResults] = await Promise.all([getCountryList(), getTooltips('fines.CourtForm')]);
        setCountryList(countryResults);
        setTooltips(tooltipResults);
        setLoading(false);
      } catch (error) {
        navigateToError();
      }
    };
    fetchData();
  }, []);

  const displayErrorsCount = (errorCount: number, touchedFields: number): void => {
    if (touchedFields === 0) {
      setActiveError(true);
    } else if (errorCount > 0) {
      setActiveError(true);
    } else {
      setActiveError(false);
    }
  };

  const clearApiError = () => {
    setApiError({
      Error: '',
    });
  };

  const postObligation = async (formikContext: any): Promise<void> => {
    window.scrollTo(0, 0);
    setLoading(true);
    // remove whitespaces before and after obligation number
    const trimmedObligationNumber = formikContext.values.OBLIGATION_NUMBER.trim();
    formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', trimmedObligationNumber);
    const payload = {
      obligationNumber: trimmedObligationNumber,
    };
    const url = `${API_URL}sitecore/Court/ValidateObligation`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 422 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        const nextPageUrl = `${pathNameLessLastSegment}${responseBody}`;
        clearApiError();
        navigate(nextPageUrl);
        window.scrollTo(0, 0);
        return;
      }
      navigateToError();
    } catch (error) {
      navigateToError();
    }
  };

  const checkExistingForm = async (formikContext: any): Promise<void> => {
    setLoading(true);
    const payload = {
      ObligationNO: formikContext.values.OBLIGATION_NUMBER,
      formType: 'Court',
    };
    const url = `${API_URL}sitecore/PartialForms/CheckingExistingForm`;

    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 200) {
        if (responseBody.PartialFormID) {
          formikValuesRef.current.setFieldValue('SaveYesNo', 'true');
          formikValuesRef.current.setFieldValue('PartialFormID', responseBody.PartialFormID);
        }
        if (responseBody.DebtorID) formikValuesRef.current.setFieldValue('DebtorID', responseBody.DebtorID);
        if (responseBody.CheckingExisting) formikValuesRef.current.setFieldValue('CheckingExisting', responseBody.CheckingExisting);
        if (responseBody.ExpiryDate) formikValuesRef.current.setFieldValue('PartialFormExpiryDate', responseBody.ExpiryDate);
        if (responseBody.CheckingExisting === 'no' && responseBody.CanSave) {
          navigate(`${pathNameLessLastSegment}/step3`);
          window.scrollTo(0, 0);
          return;
        }
        navigate(`${pathNameLessLastSegment}/step4`);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      navigateToError();
    }
  };

  const validateDebtor = async (formikContext: any): Promise<void> => {
    window.scrollTo(0, 0);
    setLoading(true);
    // Backend api is checking date time for some reasons, time need to be 14:00:00
    const getOffenceDate = formikContext.values.OFFENCE_DATE && getDateForValidateDebtor(formikContext.values.OFFENCE_DATE.toLocaleDateString('en-AU'));
    const getHearingDate = formikContext.values.HEARING_DATE && getDateForValidateDebtor(formikContext.values.HEARING_DATE.toLocaleDateString('en-AU'));
    const getDOBDate = formikContext.values.DOB && getDateForValidateDebtor(formikContext.values.DOB.toLocaleDateString('en-AU'));
    const payload = {
      dateOfBirth: getDOBDate,
      hearingDate: getHearingDate,
      licenceNumber: formikContext.values.DRIVER_LICENCE,
      obligationNumber: formikContext.values.OBLIGATION_NUMBER,
      offenceDate: getOffenceDate,
      registrationNumber: formikContext.values.VEHICLE_REGISTRATION,
    };
    const url = `${API_URL}sitecore/Court/ValidateDebtor`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        setApiError({
          Error: '',
        });
        if (responseBody === '/step4') {
          checkExistingForm(formikContext);
        }
        if (responseBody === '/step8' || responseBody === '/step9') {
          navigate(`${location.pathname.split('/').slice(0, -1).join('/')}${responseBody}`);
          window.scrollTo(0, 0);
        }
      }
    } catch (error) {
      navigateToError();
    }
  };

  const step4NextPage = (formikContext: any) => {
    window.scrollTo(0, 0);
    if (formikContext.values.DebtorType === 'Individual') {
      navigate(`${pathNameLessLastSegment}/step5`);
      window.scrollTo(0, 0);
    } else if (formikContext.values.DebtorType === 'Company') {
      navigate(`${pathNameLessLastSegment}/step6`);
      window.scrollTo(0, 0);
    } else {
      navigate(`${pathNameLessLastSegment}/step7`);
      window.scrollTo(0, 0);
    }
  };

  const createPartialForm = async (formikContext: any): Promise<void> => {
    window.scrollTo(0, 0);
    if (formikContext.values.SaveYesNo === 'false' || getIn(formikContext.values, 'PartialFormID')) {
      navigateToNextStep(navigate, location, 'next');
      return;
    }
    setLoading(true);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formikContext.values.DebtorID}`,
      EMRemeinderSent: false,
      Email: formikContext.values.saveEmail,
      ExpiryDate: '',
      FormData: JSON.stringify({ a: '1' }),
      FormTypeID: 5,
      ID: 0,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      Mobile: formikContext.values.saveMobile,
      Obligations: [
        {
          ID: 0,
          ObligationNO: formikContext.values.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 200) {
        if (responseBody.ID) formikValuesRef.current.setFieldValue('PartialFormID', responseBody.ID);
        navigateToNextStep(navigate, location, 'next');
        return;
      }
      navigateToError();
    } catch (error) {
      navigateToError();
    }
  };
  const updateFormDataObj = (formikContext: any): iPartialFormData => {
      formikContext.FormData.user.givenName = formikContext.GivenNames;
      formikContext.FormData.user.familyName = formikContext.FamilyName;
      formikContext.FormData.user.email = formikContext.NoEmail ? null : formikContext.Email;
      formikContext.FormData.user.noEmail = formikContext.NoEmail;
      formikContext.FormData.sendcopy = formikContext.EMAIL_TO === 'true';
      formikContext.FormData.user.title = formikContext.TitleMrMrsMsMissOther.toUpperCase();
      formikContext.FormData.user.dateOfBirth.day = formikContext.DOB.getDate().toString();
      formikContext.FormData.user.dateOfBirth.month = (formikContext.DOB.getMonth() + 1).toString();
      formikContext.FormData.user.dateOfBirth.year = formikContext.DOB.getFullYear().toString();
      formikContext.FormData.user.licence.number = formikContext.DRIVER_LICENCE;
      formikContext.FormData.user.licence.country = formikContext.StatecountryOfIssue;
      formikContext.FormData.user.licence.state = formikContext.LicenceState;
      formikContext.FormData.user.vehicleRegos[0].number = formikContext.VEHICLE_REGISTRATION;
      if (formikContext.DebtorType === 'Individual' || formikContext.DebtorType === '') {
        formikContext.FormData.user.postalAddress.line1 = formikContext.SummonsAddress.LineOne;
        formikContext.FormData.user.postalAddress.line2 = formikContext.SummonsAddress.LineTwo;
        formikContext.FormData.user.postalAddress.suburb = formikContext.SummonsAddress.Suburb;
        formikContext.FormData.user.postalAddress.state = formikContext.SummonsAddress.State;
        formikContext.FormData.user.postalAddress.country = formikContext.SummonsAddress.Country;
        formikContext.FormData.user.postalAddress.postcode = formikContext.SummonsAddress.Postcode;
        formikContext.FormData.user.residentialAddress.hasDifferentResidentialAddress = formikContext.HasDifferentResidentialAddress;
        formikContext.FormData.user.residentialAddress.line1 = formikContext.ResidentialAddress.LineOne;
        formikContext.FormData.user.residentialAddress.line2 = formikContext.ResidentialAddress.LineTwo;
        formikContext.FormData.user.residentialAddress.suburb = formikContext.ResidentialAddress.Suburb;
        formikContext.FormData.user.residentialAddress.state = formikContext.ResidentialAddress.State;
        formikContext.FormData.user.residentialAddress.country = formikContext.ResidentialAddress.Country;
        formikContext.FormData.user.residentialAddress.postcode = formikContext.ResidentialAddress.Postcode;
      }
      if (formikContext.DebtorType === 'Company' || formikContext.DebtorType === '') {
        formikContext.FormData.company.postalAddress.line1 = formikContext.CompanyAddress.LineOne;
        formikContext.FormData.company.postalAddress.line2 = formikContext.CompanyAddress.LineTwo;
        formikContext.FormData.company.postalAddress.suburb = formikContext.CompanyAddress.Suburb;
        formikContext.FormData.company.postalAddress.state = formikContext.CompanyAddress.State;
        formikContext.FormData.company.postalAddress.country = formikContext.CompanyAddress.Country;
        formikContext.FormData.company.postalAddress.postcode = formikContext.CompanyAddress.Postcode;
        formikContext.FormData.company.residentialAddress.hasDifferentResidentialAddress = formikContext.HasDifferentCompanyAddress;
        formikContext.FormData.company.residentialAddress.line1 = formikContext.CompanyPostalAddress.LineOne;
        formikContext.FormData.company.residentialAddress.line2 = formikContext.CompanyPostalAddress.LineTwo;
        formikContext.FormData.company.residentialAddress.suburb = formikContext.CompanyPostalAddress.Suburb;
        formikContext.FormData.company.residentialAddress.state = formikContext.CompanyPostalAddress.State;
        formikContext.FormData.company.residentialAddress.country = formikContext.CompanyPostalAddress.Country;
        formikContext.FormData.company.residentialAddress.postcode = formikContext.CompanyPostalAddress.Postcode;
        formikContext.FormData.company.companyPosition.position = formikContext.PositionOfThePersonWithinTheCompany;
        formikContext.FormData.company.companyPosition.other = formikContext.OtherPosition;
        formikContext.FormData.company.companyName = formikContext.CompanyName;
        formikContext.FormData.company.option = formikContext.AbnacnType;
        formikContext.FormData.company.abn = formikContext.AbnNo;
        formikContext.FormData.company.acn = formikContext.AcnNo;
      }
      formikContext.FormData.user.primaryContact.number = formikContext.CourtContactNumber.ContactNumber;
      formikContext.FormData.user.primaryContact.type = formikContext.CourtContactNumber.NumberType;
      formikContext.FormData.user.secondaryContact.number = formikContext.CourtContactNumber.SecondaryContactNumber;
      formikContext.FormData.user.secondaryContact.type = formikContext.CourtContactNumber.SecondaryNumberType;
      const partialformPayload: iPartialFormData = {
        ID: formikContext.ID,
        Obligations: formikContext.Obligations,
        Attachments: formikContext.Attachments,
        FormData: JSON.stringify(formikContext.FormData),
        DebtorID: formikContext.DebtorID,
        FormTypeID: formikContext.FormTypeID,
        Email: formikContext.saveEmail,
        Mobile: formikContext.saveMobile,
        IsSubscribedEM: formikContext.IsSubscribedEM,
        IsSubscribedSMS: formikContext.IsSubscribedSMS,
        EMRemeinderSent: formikContext.EMRemeinderSent,
        SMSRemeinderSent: formikContext.SMSRemeinderSent,
        CreateDate: formikContext.CreateDate,
        ExpiryDate: formikContext.ExpiryDate,
      };
    return partialformPayload;
  };

  const createPartialFormForSumbit = async (formikContext: any): Promise<void> => {
    const partialFormObj = updateFormDataObj(formikContext);
    const payload = partialFormObj;
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
     await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }).then((partialRes) => {
        if (partialRes.status !== 200) {
          formikValuesRef.current.setFieldValue('submissionError', partialRes.statusText);
          navToError(navigate, location);
        }
      }).catch((error) => {
        formikValuesRef.current.setFieldValue('submissionError', error);
        navToError(navigate, location);
      });
  };
  const updatePartialForm = async (formikContext: any): Promise<void> => {
    window.scrollTo(0, 0);
    formikValuesRef.current.setFieldTouched('FamilyName');
    if (formikContext.values.PartialFormID) {
      setLoading(true);
      formikContext.values.FormData.user.givenName = formikContext.values.GivenNames;
      formikContext.values.FormData.user.familyName = formikContext.values.FamilyName;
      formikContext.values.FormData.user.email = formikContext.values.NoEmail ? null : formikContext.values.Email;
      formikContext.values.FormData.user.noEmail = formikContext.values.NoEmail;
      formikContext.values.FormData.sendcopy = formikContext.values.EMAIL_TO === 'true';
      formikContext.values.FormData.user.title = formikContext.values.TitleMrMrsMsMissOther.toUpperCase();
      formikContext.values.FormData.user.dateOfBirth.day = formikContext.values.DOB.getDate().toString();
      formikContext.values.FormData.user.dateOfBirth.month = (formikContext.values.DOB.getMonth() + 1).toString();
      formikContext.values.FormData.user.dateOfBirth.year = formikContext.values.DOB.getFullYear().toString();
      formikContext.values.FormData.user.licence.number = formikContext.values.DRIVER_LICENCE;
      formikContext.values.FormData.user.licence.country = formikContext.values.StatecountryOfIssue;
      formikContext.values.FormData.user.licence.state = formikContext.values.LicenceState;
      formikContext.values.FormData.user.vehicleRegos[0].number = formikContext.values.VEHICLE_REGISTRATION;
      if (formikContext.values.DebtorType === 'Individual' || formikContext.values.DebtorType === '') {
        formikContext.values.FormData.user.postalAddress.line1 = formikContext.values.SummonsAddress.LineOne;
        formikContext.values.FormData.user.postalAddress.line2 = formikContext.values.SummonsAddress.LineTwo;
        formikContext.values.FormData.user.postalAddress.suburb = formikContext.values.SummonsAddress.Suburb;
        formikContext.values.FormData.user.postalAddress.state = formikContext.values.SummonsAddress.State;
        formikContext.values.FormData.user.postalAddress.country = formikContext.values.SummonsAddress.Country;
        formikContext.values.FormData.user.postalAddress.postcode = formikContext.values.SummonsAddress.Postcode;
        formikContext.values.FormData.user.residentialAddress.hasDifferentResidentialAddress = formikContext.values.HasDifferentResidentialAddress;
        formikContext.values.FormData.user.residentialAddress.line1 = formikContext.values.ResidentialAddress.LineOne;
        formikContext.values.FormData.user.residentialAddress.line2 = formikContext.values.ResidentialAddress.LineTwo;
        formikContext.values.FormData.user.residentialAddress.suburb = formikContext.values.ResidentialAddress.Suburb;
        formikContext.values.FormData.user.residentialAddress.state = formikContext.values.ResidentialAddress.State;
        formikContext.values.FormData.user.residentialAddress.country = formikContext.values.ResidentialAddress.Country;
        formikContext.values.FormData.user.residentialAddress.postcode = formikContext.values.ResidentialAddress.Postcode;
      }
      if (formikContext.values.DebtorType === 'Company' || formikContext.values.DebtorType === '') {
        formikContext.values.FormData.company.postalAddress.line1 = formikContext.values.CompanyAddress.LineOne;
        formikContext.values.FormData.company.postalAddress.line2 = formikContext.values.CompanyAddress.LineTwo;
        formikContext.values.FormData.company.postalAddress.suburb = formikContext.values.CompanyAddress.Suburb;
        formikContext.values.FormData.company.postalAddress.state = formikContext.values.CompanyAddress.State;
        formikContext.values.FormData.company.postalAddress.country = formikContext.values.CompanyAddress.Country;
        formikContext.values.FormData.company.postalAddress.postcode = formikContext.values.CompanyAddress.Postcode;
        formikContext.values.FormData.company.residentialAddress.hasDifferentResidentialAddress = formikContext.values.HasDifferentCompanyAddress;
        formikContext.values.FormData.company.residentialAddress.line1 = formikContext.values.CompanyPostalAddress.LineOne;
        formikContext.values.FormData.company.residentialAddress.line2 = formikContext.values.CompanyPostalAddress.LineTwo;
        formikContext.values.FormData.company.residentialAddress.suburb = formikContext.values.CompanyPostalAddress.Suburb;
        formikContext.values.FormData.company.residentialAddress.state = formikContext.values.CompanyPostalAddress.State;
        formikContext.values.FormData.company.residentialAddress.country = formikContext.values.CompanyPostalAddress.Country;
        formikContext.values.FormData.company.residentialAddress.postcode = formikContext.values.CompanyPostalAddress.Postcode;
        formikContext.values.FormData.company.companyPosition.position = formikContext.values.PositionOfThePersonWithinTheCompany;
        formikContext.values.FormData.company.companyPosition.other = formikContext.values.OtherPosition;
        formikContext.values.FormData.company.companyName = formikContext.values.CompanyName;
        formikContext.values.FormData.company.option = formikContext.values.AbnacnType;
        formikContext.values.FormData.company.abn = formikContext.values.AbnNo;
        formikContext.values.FormData.company.acn = formikContext.values.AcnNo;
      }
      formikContext.values.FormData.user.primaryContact.number = formikContext.values.CourtContactNumber.ContactNumber;
      formikContext.values.FormData.user.primaryContact.type = formikContext.values.CourtContactNumber.NumberType;
      formikContext.values.FormData.user.secondaryContact.number = formikContext.values.CourtContactNumber.SecondaryContactNumber;
      formikContext.values.FormData.user.secondaryContact.type = formikContext.values.CourtContactNumber.SecondaryNumberType;
      const partialformPayload: iPartialFormData = {
        ID: formikContext.values.ID,
        Obligations: formikContext.values.Obligations,
        Attachments: formikContext.values.Attachments,
        FormData: JSON.stringify(formikContext.values.FormData),
        DebtorID: formikContext.values.DebtorID,
        FormTypeID: formikContext.values.FormTypeID,
        Email: formikContext.values.saveEmail,
        Mobile: formikContext.values.saveMobile,
        IsSubscribedEM: formikContext.values.IsSubscribedEM,
        IsSubscribedSMS: formikContext.values.IsSubscribedSMS,
        EMRemeinderSent: formikContext.values.EMRemeinderSent,
        SMSRemeinderSent: formikContext.values.SMSRemeinderSent,
        CreateDate: formikContext.values.CreateDate,
        ExpiryDate: formikContext.values.ExpiryDate,
      };
      try {
        const response = await savePartialForm(partialformPayload);
        setLoading(false);
        if (response) {
          navigate(`${pathNameLessLastSegment}/step10`);
          window.scrollTo(0, 0);
          return;
        }
        navigateToError();
      } catch (error) {
        navigateToError();
      }
    } else {
      navigate(`${pathNameLessLastSegment}/step10`);
      window.scrollTo(0, 0);
    }
  };

  const submitForm = async (formValues: iCourtFormValues): Promise<boolean> => {
    setLoading(true);
    const submissionNumber = 'SubmissionNumber';
    const formSubmissionValues = getSubmissionPayload(formValues, submissionNumber);
    const submitPayload = {
      data: JSON.stringify(formSubmissionValues),
      obligationNo: formValues.OBLIGATION_NUMBER
    };

    const urlSubmission = formValues.SeriousObligation ? `${API_URL}sitecore/Court/SubmitNoticeOfObjection` : `${API_URL}sitecore/Court/SubmitCourtHearing`;
    let submitResponse = false;
    submitResponse = await fetch(urlSubmission, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitPayload),
      })
      .then((formSubResponse) => {
        if (formSubResponse.status === 200) {
          if (formValues.SaveYesNo === 'true') {
            // having partial form, close it
            submitSavedPartialForm(formValues.PartialFormID.toString());
          }
          formikValuesRef.current.setFieldValue('submitted', true);
          ReactGA.event({
            category: 'Court Form',
            action: 'Submit',
            label: 'Submit Court Form',
          });
          return formSubResponse.json();
        }
        formikValuesRef.current.setFieldValue('submissionError', formSubResponse.statusText);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        navToError(navigate, location);
        return false;
      }).then((response) => {
        if (response) {
          formikValuesRef.current.setFieldValue('submissionNo', response.receiptNo); // get submission number from API response
          return true;
        }
        return false;
      }).catch((error) => {
        formikValuesRef.current.setFieldValue('submissionError', error);
        if (formValues.SaveYesNo === 'false') {
          createPartialFormForSumbit(formValues);
        }
        navToError(navigate, location);
        return false;
      })
      .finally(() => {
        setLoading(false);
      });
      if (!submitResponse) return false;
      return true;
  };

  const formNavActions: iFormNavActions = {
    step1: {
      next: postObligation,
    },
    step2: {
      next: validateDebtor,
    },
    step3: {
      next: createPartialForm,
    },
    step4: {
      next: step4NextPage,
    },
    step5: {
      next: updatePartialForm,
    },
    step6: {
      next: updatePartialForm,
    },
  };

  return (
    <Formik
      initialValues={defaultForm()}
      innerRef={formikValuesRef}
      validationSchema={CourtSchema}
      onSubmit={(values: iCourtFormValues) => {
        submitForm(values);
      }}>
      {({ values }) => (
        <VicFinesContext.Provider value={{ countryList, setCountryList, tooltips, setTooltips, apiError, clearApiError }}>
          <FormContainer title="Court">
            {loading && <FormLoading />}
            {!loading && (
              <Form>
                {!location.pathname.includes('error') && <ProgressBar formMetaData={courtMetaData} />}
                {activeError && <Alert title="Sorry there is a problem" icon={IssueIcon} color="red" message={<ErrorContent />} theme="error" />}
                <Routes>
                  <Route path="/step0/:ob" element={<CourtStep0 />} />
                  <Route path="/step1" element={<CourtStep1 />} />
                  <Route path="/step2" element={<CourtStep2 />} />
                  <Route path="/step3" element={<CourtStep3 />} />
                  <Route path="/step4" element={<CourtStep4 />} />
                  <Route path="/step5" element={<CourtStep5 />} />
                  <Route path="/step6" element={<CourtStep6 />} />
                  <Route path="/step7" element={<CourtStep7 />} />
                  <Route path="/step8" element={<CourtStep8 />} />
                  <Route path="/step9" element={<CourtStep9 />} />
                  <Route path="/step10" element={<CourtStep10 />} />
                  <Route path="/step11" element={<CourtStep11 />} />
                  <Route path="/step12" element={<CourtStep12 />} />
                  <Route path="/error" element={<CourtErrorPage />} />
                  <Route path="/" element={<CourtStep0 />} />
                </Routes>
                {!(
                  location.pathname === `${PATH_COURT}/step8` ||
                  location.pathname === `${PATH_COURT}/step9` ||
                  location.pathname === `${PATH_COURT}/step12` ||
                  location.pathname === `${PATH_COURT}/error`
                ) && (
                  <FormNavFooter
                    totalPages={13}
                    formNavActions={formNavActions}
                    submitPage={11}
                    submit={async () => submitForm(values)}
                    tooltips={tooltips}
                    displayErrorsCount={displayErrorsCount}
                    pageValidationKeys={courtMetaData.pagesSegments[currentStep].validationKeys(values)}
                    formType={FormType.COURT}
                    firstPageNo={0}
                  />
                )}
              </Form>

            )}
          </FormContainer>
        </VicFinesContext.Provider>
      )}
    </Formik>
  );
}

export default Court;
